import {
  compsTableGetCompsTableOptions,
  extractErrorMessage,
  useHeyApi,
} from "@platform/api";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  buildCompsColDefs,
  buildCompsContext,
  buildCompsRowData,
} from "./buildCompsGridData";

import type { CompsTableCompsTableResponse } from "@platform/api";
import type { ColDef } from "ag-grid-community";
import type { CompsGridContext, CompsGridRow } from "./types";

interface UseCompsTableOptions {
  ecoRfpId: string;
  /**
   * The analyst's shared SBS carrier sequence, applied from the Quotes tab
   * across Responses/Comps/Financials (CD-1806). Undefined/empty falls back
   * to the backend's `sequence` order.
   */
  carrierOrder?: readonly string[];
}

interface UseCompsTableResult {
  rowData: CompsGridRow[];
  colDefs: ColDef<CompsGridRow>[];
  context: CompsGridContext;
  isLoading: boolean;
  errorMessage: string | null;
}

/**
 * Fetches the coverages × carriers comps matrix for an RFP and derives
 * AG Grid row data and column definitions from the result.
 *
 * Uses the centro-portal BFF endpoint (`/rfps/{id}/comps-table`) via the
 * `centroPortalClient`.
 */
export const useCompsTable = ({
  ecoRfpId,
  carrierOrder,
}: UseCompsTableOptions): UseCompsTableResult => {
  const { centroPortalClient } = useHeyApi();

  // Memoized so React Query only re-derives grid data when the response actually
  // changes (i.e. on refetch). An inline select runs on every render and returns
  // a new rowData array each time, which resets the grid's local row state and
  // wipes AG Grid's in-place optimistic edit — the cell would blank out and only
  // reappear once the refetch lands. Stable identity lets the optimistic edit
  // persist until the server value reconciles it. carrierOrder is a dependency
  // so a Quotes-tab reorder (CD-1806) re-derives colDefs without needing a
  // refetch.
  const select = useCallback(
    (response: CompsTableCompsTableResponse) => ({
      rowData: buildCompsRowData(response),
      colDefs: buildCompsColDefs(response, carrierOrder),
      context: buildCompsContext(response),
    }),
    [carrierOrder],
  );

  const { data, isLoading, error } = useQuery({
    ...compsTableGetCompsTableOptions({
      path: { id: ecoRfpId },
      client: centroPortalClient,
    }),
    enabled: !!ecoRfpId,
    staleTime: 5 * 60 * 1000,
    select,
  });

  return {
    rowData: data?.rowData ?? [],
    colDefs: data?.colDefs ?? [],
    context: data?.context ?? {
      proposalIdByCarrierId: new Map(),
      proposalIdByCellKey: new Map(),
      proposalPlanIdByCellKey: new Map(),
      materializeTargetByRowGroupId: new Map(),
      freeTextFieldByRowId: new Map(),
    },
    isLoading,
    errorMessage: error
      ? extractErrorMessage(error, "Failed to load comps table.")
      : null,
  };
};
